import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useCart } from '../../context/CartContext';
import { getBadgeStyle } from '../dashboard/store/ProductsPanel';

const { FiX, FiShoppingCart, FiPackage, FiChevronLeft, FiChevronRight } = FiIcons;

const ProductModal = ({ product, onClose }) => {
  const { addToCart, storeStyles: s } = useCart();
  const [activeIdx, setActiveIdx] = useState(0);

  const accent      = s.productAccent      || '#14b8a6';
  const productBg   = s.productBg          || '#ffffff';
  const imageBg     = s.productImageBg     || '#f3f4f6';
  const titleColor  = s.productTitleColor  || '#111827';
  const textColor   = s.productTextColor   || '#4b5563';
  const modalRadius = s.productRadius      || '1.5rem';
  const btnRadius   = s.productButtonRadius|| '0.75rem';

  const price        = Number(product?.price || 0);
  const comparePrice = Number(product?.compare_at_price || 0);
  const isOnSale     = !product?.badge && (product?.on_sale || (comparePrice > 0 && comparePrice > price));
  const badgeStyle   = product?.badge ? getBadgeStyle(product.badge) : null;

  const images = (() => {
    if (Array.isArray(product?.images) && product.images.length > 0) return product.images;
    if (product?.image_url) return [product.image_url];
    return [];
  })();
  const hasMultiple = images.length > 1;

  const prev = (e) => { e.stopPropagation(); setActiveIdx(i => (i - 1 + images.length) % images.length); };
  const next = (e) => { e.stopPropagation(); setActiveIdx(i => (i + 1) % images.length); };

  if (!product) return null;

  return (
    <div
      className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/65 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.96, y: 24 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.96, y: 24 }}
        transition={{ type: 'spring', stiffness: 320, damping: 28 }}
        onClick={(e) => e.stopPropagation()}
        className="shadow-2xl w-full max-w-4xl flex flex-col md:flex-row relative"
        style={{
          backgroundColor: productBg,
          borderRadius: modalRadius,
          maxHeight: '90vh',
          overflow: 'hidden',
        }}
      >
        {/* ── Close ── */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-30 w-9 h-9 bg-white/90 backdrop-blur rounded-full flex items-center justify-center shadow-md transition-all hover:bg-white hover:scale-110"
          style={{ color: titleColor }}
        >
          <SafeIcon icon={FiX} className="w-5 h-5" />
        </button>

        {/* ── Image Side ── */}
        <div
          className="md:w-1/2 flex flex-col shrink-0"
          style={{ backgroundColor: imageBg, minHeight: 320 }}
        >
          {/* Main image — fixed aspect-square container */}
          <div className="relative w-full" style={{ paddingTop: '100%' }}>
            <div className="absolute inset-0 overflow-hidden" style={{ backgroundColor: imageBg }}>
              <AnimatePresence mode="wait">
                {images.length > 0 ? (
                  <motion.img
                    key={activeIdx}
                    src={images[activeIdx]}
                    alt={`${product.name} ${activeIdx + 1}`}
                    initial={{ opacity: 0, x: 30 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -30 }}
                    transition={{ duration: 0.22 }}
                    style={{
                      position: 'absolute',
                      inset: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain',
                      objectPosition: 'center',
                      padding: '16px',
                    }}
                  />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <SafeIcon icon={FiPackage} className="w-20 h-20 text-gray-300" />
                  </div>
                )}
              </AnimatePresence>

              {/* Badge */}
              {badgeStyle && (
                <div
                  className="absolute top-4 left-4 z-10 text-[10px] font-black uppercase tracking-wider px-3 py-1.5 rounded-full shadow-lg"
                  style={{ backgroundColor: badgeStyle.bg, color: badgeStyle.text }}
                >
                  {product.badge}
                </div>
              )}
              {isOnSale && !badgeStyle && (
                <div className="absolute top-4 left-4 z-10 text-white text-[10px] font-black uppercase tracking-wider px-3 py-1.5 rounded-full shadow-lg bg-red-500">
                  Sale
                </div>
              )}

              {/* Nav arrows */}
              {hasMultiple && (
                <>
                  <button onClick={prev}
                    className="absolute left-3 top-1/2 -translate-y-1/2 z-10 w-9 h-9 bg-white/85 hover:bg-white backdrop-blur rounded-full flex items-center justify-center shadow-md transition-all hover:scale-105">
                    <SafeIcon icon={FiChevronLeft} className="w-5 h-5 text-gray-700" />
                  </button>
                  <button onClick={next}
                    className="absolute right-3 top-1/2 -translate-y-1/2 z-10 w-9 h-9 bg-white/85 hover:bg-white backdrop-blur rounded-full flex items-center justify-center shadow-md transition-all hover:scale-105">
                    <SafeIcon icon={FiChevronRight} className="w-5 h-5 text-gray-700" />
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Thumbnails */}
          {hasMultiple && (
            <div
              className="flex gap-2 p-3 overflow-x-auto shrink-0 border-t border-black/5"
              style={{ backgroundColor: imageBg }}
            >
              {images.map((url, idx) => (
                <button
                  key={idx}
                  onClick={(e) => { e.stopPropagation(); setActiveIdx(idx); }}
                  className="shrink-0 rounded-lg overflow-hidden border-2 transition-all"
                  style={{
                    width: 52, height: 52,
                    borderColor: idx === activeIdx ? accent : 'transparent',
                    opacity: idx === activeIdx ? 1 : 0.55,
                    transform: idx === activeIdx ? 'scale(1.06)' : 'scale(1)',
                  }}
                >
                  <img src={url} alt={`Thumb ${idx + 1}`} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* ── Details Side ── */}
        <div
          className="md:w-1/2 flex flex-col"
          style={{
            backgroundColor: productBg,
            maxHeight: '90vh',
            overflow: 'hidden',
          }}
        >
          <div className="flex flex-col h-full p-8 md:p-10">
            {/* Category */}
            {product.category && (
              <span className="text-[10px] font-black uppercase tracking-widest mb-3 block" style={{ color: accent }}>
                {product.category.split(',')[0]}
              </span>
            )}

            {/* Title */}
            <h2 className="text-2xl md:text-3xl font-black leading-tight mb-4" style={{ color: titleColor }}>
              {product.name}
            </h2>

            {/* Price */}
            <div className="flex items-baseline gap-3 mb-4">
              <span className="text-3xl font-black" style={{ color: titleColor }}>
                R{price.toFixed(2)}
              </span>
              {isOnSale && comparePrice > price && (
                <span className="text-lg line-through font-medium" style={{ color: textColor }}>
                  R{comparePrice.toFixed(2)}
                </span>
              )}
            </div>

            {/* Stock indicator */}
            <div className="flex items-center gap-2 mb-5 px-3 py-2 rounded-xl" style={{ backgroundColor: imageBg }}>
              <span
                className="w-2.5 h-2.5 rounded-full shrink-0"
                style={{ backgroundColor: product.stock > 0 ? '#22c55e' : '#ef4444' }}
              />
              <span className="text-sm font-bold" style={{ color: product.stock > 0 ? titleColor : '#ef4444' }}>
                {product.stock > 0 ? `${product.stock} in stock` : 'Out of stock'}
              </span>
            </div>

            {/* Description — scrollable */}
            <div className="mb-6 flex-1 flex flex-col overflow-hidden">
              <h4 className="text-[10px] font-black uppercase tracking-widest mb-2 shrink-0" style={{ color: textColor }}>
                Description
              </h4>
              <div
                className="leading-relaxed text-sm overflow-y-auto pr-2 flex-1"
                style={{ color: textColor, maxHeight: '180px' }}
                dangerouslySetInnerHTML={{
                  __html: product.description || '<p style="opacity:0.5">No description available.</p>',
                }}
              />
            </div>

            {/* ── Add to Cart Button — always visible at bottom ── */}
            <div className="pt-5 border-t border-gray-100 shrink-0 mt-auto">
              <button
                onClick={() => { addToCart(product); onClose(); }}
                disabled={product.stock <= 0}
                className="w-full flex items-center justify-center gap-3 py-4 font-black text-base transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed text-white"
                style={{
                  backgroundColor: product.stock > 0 ? '#111827' : '#d1d5db',
                  borderRadius: btnRadius,
                }}
                onMouseEnter={e => { if (product.stock > 0) { e.currentTarget.style.backgroundColor = '#000000'; e.currentTarget.style.transform = 'translateY(-2px)'; } }}
                onMouseLeave={e => { if (product.stock > 0) { e.currentTarget.style.backgroundColor = '#111827'; e.currentTarget.style.transform = 'translateY(0)'; } }}
              >
                <SafeIcon icon={FiShoppingCart} className="w-5 h-5" />
                <span>{product.stock > 0 ? 'Add to Cart' : 'Out of Stock'}</span>
              </button>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default ProductModal;